import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { 
  MessageSquare, Phone, Mail, Send, Plus, Clock,
  ArrowUpRight, ArrowDownLeft, User, RefreshCw, ExternalLink, Maximize2, X, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const JobCommunicationsTab = ({ job }) => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState('all');
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const [lastMessageCount, setLastMessageCount] = useState(0);

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (job?.id) {
      fetchConversations();
    }
    
    // Cleanup polling on unmount
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [job?.id]);

  // Auto-poll for new messages when a conversation is selected
  useEffect(() => {
    // Clear any existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    if (selectedConversation?.id) {
      // Poll every 5 seconds for new messages
      pollIntervalRef.current = setInterval(() => {
        fetchMessagesQuiet(selectedConversation.id);
      }, 5000);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [selectedConversation?.id]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messages.length > lastMessageCount) {
      scrollToBottom();
    }
    setLastMessageCount(messages.length);
  }, [messages.length]);

  const fetchConversations = async () => {
    setLoading(true);
    try {
      // Fetch conversations for this specific job
      const response = await axios.get(`${API}/communications/conversations`, {
        params: { job_id: job.id }
      });
      // Handle both array and {conversations: [...]} formats
      const data = response.data?.conversations || (Array.isArray(response.data) ? response.data : []);
      setConversations(data);
      
      // Auto-select first conversation
      if (data.length > 0 && !selectedConversation) {
        setSelectedConversation(data[0]);
        fetchMessages(data[0].id);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/communications/conversations/${conversationId}/messages`);
      // Handle both array and {messages: [...]} formats
      const data = response.data?.messages || (Array.isArray(response.data) ? response.data : []);
      setMessages(data);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setMessages([]);
    }
  };

  // Quiet fetch for polling - doesn't show loading states
  const fetchMessagesQuiet = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/communications/conversations/${conversationId}/messages`);
      const data = response.data?.messages || (Array.isArray(response.data) ? response.data : []);
      setMessages(data);
    } catch (error) {
      // Silently fail for polling
      console.error('Polling error:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      await axios.post(`${API}/communications/conversations/${selectedConversation.id}/messages`, {
        body: newMessage,
        channel: selectedConversation.conversation_type === 'sms' ? 'sms' : 'email',
        direction: 'outbound'
      });
      
      setNewMessage('');
      fetchMessages(selectedConversation.id);
      fetchConversations();
      toast.success('Message sent');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleStartNewConversation = async (type) => {
    if (!job.customer_phone && type === 'sms') {
      toast.error('No phone number available for this customer');
      return;
    }
    if (!job.customer_email && type === 'email') {
      toast.error('No email address available for this customer');
      return;
    }

    try {
      const response = await axios.post(`${API}/communications/conversations`, {
        customer_id: job.contact_id || job.id,
        job_id: job.id,
        conversation_type: type,
        customer_name: job.customer_name,
        customer_phone: job.customer_phone,
        customer_email: job.customer_email
      });
      
      // Reset messages for new conversation
      setMessages([]);
      
      // Set the new conversation as selected (response now contains full conversation object)
      const newConversation = response.data;
      setSelectedConversation(newConversation);
      
      // Add to conversations list immediately
      setConversations(prev => [newConversation, ...prev]);
      
      toast.success(`New ${type.toUpperCase()} conversation started`);
    } catch (error) {
      console.error('Error creating conversation:', error);
      toast.error('Failed to start conversation');
    }
  };

  const handleDeleteConversation = async () => {
    if (!selectedConversation) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/communications/conversations/${selectedConversation.id}`);
      
      toast.success('Conversation deleted');
      setSelectedConversation(null);
      setMessages([]);
      fetchConversations();
    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast.error('Failed to delete conversation');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const getChannelIcon = (type) => {
    switch (type) {
      case 'sms': return <MessageSquare className="h-4 w-4" />;
      case 'call': return <Phone className="h-4 w-4" />;
      case 'email': return <Mail className="h-4 w-4" />;
      default: return <MessageSquare className="h-4 w-4" />;
    }
  };

  const getChannelColor = (type) => {
    switch (type) {
      case 'sms': return 'bg-green-500/20 text-green-400';
      case 'call': return 'bg-blue-500/20 text-blue-400';
      case 'email': return 'bg-purple-500/20 text-purple-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  const filteredConversations = filter === 'all' 
    ? conversations 
    : conversations.filter(c => c.conversation_type === filter);

  // Auto-select first conversation when filter changes
  useEffect(() => {
    if (filteredConversations.length > 0) {
      const currentConvInFilter = filteredConversations.find(c => c.id === selectedConversation?.id);
      if (!currentConvInFilter) {
        setSelectedConversation(filteredConversations[0]);
        fetchMessages(filteredConversations[0].id);
      }
    } else {
      setSelectedConversation(null);
      setMessages([]);
    }
  }, [filter, filteredConversations.length]);

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  if (!job) return null;

  return (
    <div className="h-full flex flex-col">
      {/* Fixed Header with Channel Filters */}
      <div className="flex-shrink-0 bg-slate-900 border-b border-slate-800 py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Channel Filter Tabs */}
            <div className="flex items-center gap-1 bg-slate-800 rounded-lg p-1">
              <Button
                variant={filter === 'all' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setFilter('all')}
                className="h-8 px-4"
              >
                All
              </Button>
              <Button
                variant={filter === 'sms' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setFilter('sms')}
                className="h-8 px-4"
              >
                <MessageSquare className="h-4 w-4 mr-1" />
                SMS
              </Button>
              <Button
                variant={filter === 'call' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setFilter('call')}
                className="h-8 px-4"
              >
                <Phone className="h-4 w-4 mr-1" />
                Calls
              </Button>
              <Button
                variant={filter === 'email' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setFilter('email')}
                className="h-8 px-4"
              >
                <Mail className="h-4 w-4 mr-1" />
                Email
              </Button>
            </div>
            
            {/* Customer Info */}
            <div className="border-l border-slate-700 pl-4">
              <p className="font-medium text-sm text-white">{job.customer_name}</p>
              <p className="text-xs text-slate-400">{job.customer_phone}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleStartNewConversation('sms')}
              disabled={!job.customer_phone}
              className="border-slate-700"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              New SMS
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleStartNewConversation('email')}
              disabled={!job.customer_email}
              className="border-slate-700"
            >
              <Mail className="h-4 w-4 mr-2" />
              New Email
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchConversations}
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(true)}
              disabled={!selectedConversation}
              title="Expand conversation"
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/communications?job_id=${job.id}`)}
              title="Open in Communications Hub"
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={!selectedConversation}
              title="Delete conversation"
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Messages Container - Only this area should scroll */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <Card className="flex-1 bg-slate-900 border-slate-800 flex flex-col overflow-hidden">
          {selectedConversation ? (
            <>
              {/* Messages - Only this area scrolls */}
              <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center text-slate-400 py-8">
                    <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No messages yet</p>
                    <p className="text-xs mt-1">Send a message to start the conversation</p>
                  </div>
                ) : (
                  <>
                    {messages.map(msg => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-lg p-3 ${
                            msg.direction === 'outbound'
                              ? 'bg-blue-600 text-white'
                              : 'bg-slate-800 text-white'
                          }`}
                        >
                          <p className="text-sm">{msg.body}</p>
                          <div className={`flex items-center gap-1 mt-1 ${
                            msg.direction === 'outbound' ? 'justify-end' : 'justify-start'
                          }`}>
                            {msg.direction === 'outbound' ? (
                              <ArrowUpRight className="h-3 w-3 opacity-50" />
                            ) : (
                              <ArrowDownLeft className="h-3 w-3 opacity-50" />
                            )}
                            <span className="text-xs opacity-50">
                              {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </CardContent>

              {/* Compose */}
              {selectedConversation.conversation_type !== 'call' && (
                <div className="p-4 border-t border-slate-800">
                  <div className="flex gap-2">
                    <Textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder={`Type your ${selectedConversation.conversation_type} message...`}
                      className="flex-1 bg-slate-800 border-slate-700 resize-none"
                      rows={2}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                    />
                    <Button
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim() || sending}
                      className="bg-blue-600 hover:bg-blue-700 self-end"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No conversations yet</p>
                <p className="text-sm mt-1">Start a new conversation using the buttons above</p>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Expanded Conversation Modal */}
      <Dialog open={isExpanded} onOpenChange={setIsExpanded}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0 bg-slate-900 border-slate-700" aria-describedby="expanded-conv-description">
          <span id="expanded-conv-description" className="sr-only">
            Expanded view of the SMS conversation with {job?.customer_name}
          </span>
          <DialogHeader className="px-6 py-4 border-b border-slate-700 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {selectedConversation && (
                  <>
                    <Badge className={getChannelColor(selectedConversation.conversation_type)}>
                      {getChannelIcon(selectedConversation.conversation_type)}
                      <span className="ml-1 capitalize">{selectedConversation.conversation_type}</span>
                    </Badge>
                    <div>
                      <DialogTitle className="text-lg font-semibold text-white">
                        {job?.customer_name}
                      </DialogTitle>
                      <p className="text-sm text-slate-400">
                        {selectedConversation.conversation_type === 'sms' 
                          ? job?.customer_phone 
                          : job?.customer_email}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </DialogHeader>

          {/* Expanded Messages Area */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {messages.length === 0 ? (
              <div className="text-center text-slate-400 py-12">
                <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="text-lg">No messages yet</p>
                <p className="text-sm mt-1">Send a message to start the conversation</p>
              </div>
            ) : (
              messages.map(msg => (
                <div
                  key={msg.id}
                  className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[60%] rounded-xl p-4 ${
                      msg.direction === 'outbound'
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-800 text-white'
                    }`}
                  >
                    <p className="text-base leading-relaxed">{msg.body}</p>
                    <div className={`flex items-center gap-2 mt-2 ${
                      msg.direction === 'outbound' ? 'justify-end' : 'justify-start'
                    }`}>
                      {msg.direction === 'outbound' ? (
                        <ArrowUpRight className="h-3.5 w-3.5 opacity-60" />
                      ) : (
                        <ArrowDownLeft className="h-3.5 w-3.5 opacity-60" />
                      )}
                      <span className="text-xs opacity-60">
                        {new Date(msg.created_at).toLocaleString([], { 
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Expanded Compose Area */}
          {selectedConversation?.conversation_type !== 'call' && (
            <div className="px-6 py-4 border-t border-slate-700 flex-shrink-0">
              <div className="flex gap-3">
                <Textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={`Type your ${selectedConversation?.conversation_type || 'sms'} message...`}
                  className="flex-1 bg-slate-800 border-slate-600 resize-none text-base"
                  rows={3}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || sending}
                  className="bg-blue-600 hover:bg-blue-700 self-end px-6 py-3"
                  size="lg"
                >
                  <Send className="h-5 w-5" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Conversation?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This will permanently delete this conversation and all its messages. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConversation}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default JobCommunicationsTab;
